"use client";

import { Gauge } from "lucide-react";
import { useMemo, useState } from "react";

import { OfferCard } from "@/components/model-board/offer-card";
import { useApi } from "@/core/api";
import { discountFraction } from "@/core/discount";
import { getDefaultProviderMapping } from "@/core/model-status";
import { formatNumber } from "@/core/numbers";
import { cx } from "@/ds";

import type { ProviderOffers } from "./model-data";

const SORTS = {
	featured: "Featured",
	price: "Lowest price",
	speed: "Fastest",
	context: "Largest context",
} as const;
type Sort = keyof typeof SORTS;

// Throughput from fewer requests than this is noise, not a measurement.
const MIN_REQUESTS = 100;

function inputPrice(group: ProviderOffers): number | null {
	const offer = getDefaultProviderMapping(group.offers);
	const raw = Number(offer.peakPricing?.peak.inputPrice ?? offer.inputPrice);
	return Number.isFinite(raw) && offer.inputPrice !== null
		? raw * (1 - discountFraction(offer.discount))
		: null;
}

/** Every provider's offer for one model, sortable, retired ones on request. */
export function ModelOffers({
	modelId,
	groups,
}: {
	modelId: string;
	groups: ProviderOffers[];
}) {
	const api = useApi();
	const [sort, setSort] = useState<Sort>("featured");
	const [showRetired, setShowRetired] = useState(false);
	const benchmarks = api.useQuery(
		"get",
		"/internal/models/{modelId}/benchmarks",
		{
			params: { path: { modelId } },
		},
	);

	const speed = useMemo(() => {
		const map = new Map<string, number>();
		for (const row of benchmarks.data?.providers ?? []) {
			if (row.logsCount >= MIN_REQUESTS && row.tokensPerSecond !== null) {
				map.set(row.providerId, row.tokensPerSecond);
			}
		}
		return map;
	}, [benchmarks.data]);

	const retiredCount = groups.filter((group) => group.retired).length;
	const everyoneRetired = retiredCount === groups.length;
	const shown = groups.filter(
		(group) => showRetired || everyoneRetired || !group.retired,
	);

	const value = (group: ProviderOffers): number | null => {
		switch (sort) {
			case "price":
				return inputPrice(group);
			case "speed":
				return speed.get(group.providerId) ?? null;
			case "context":
				return getDefaultProviderMapping(group.offers).contextSize ?? null;
			default:
				return null;
		}
	};
	const ordered =
		sort === "featured"
			? shown
			: [...shown].sort((a, b) => {
					const left = value(a);
					const right = value(b);
					if (left === null || right === null) {
						return left === right ? 0 : left === null ? 1 : -1;
					}
					return sort === "price" ? left - right : right - left;
				});

	const sorts = (Object.keys(SORTS) as Sort[]).filter(
		(key) => key !== "speed" || speed.size > 0,
	);

	return (
		<div>
			{groups.length > 1 || retiredCount > 0 ? (
				<div className="mb-4 flex flex-wrap items-center justify-between gap-2">
					{groups.length > 1 ? (
						<div
							role="group"
							aria-label="Sort providers"
							className="flex flex-wrap gap-1 rounded-lg bg-muted p-1"
						>
							{sorts.map((key) => (
								<button
									key={key}
									type="button"
									aria-pressed={sort === key}
									onClick={() => setSort(key)}
									className={cx(
										"rounded-md px-3 py-1 text-xs font-medium",
										sort === key
											? "bg-background shadow-sm"
											: "text-muted-foreground hover:text-foreground",
									)}
								>
									{SORTS[key]}
								</button>
							))}
						</div>
					) : (
						<span />
					)}
					{retiredCount > 0 && !everyoneRetired ? (
						<button
							type="button"
							onClick={() => setShowRetired((current) => !current)}
							className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
						>
							{showRetired
								? "Hide retired providers"
								: `Show ${retiredCount} retired provider${retiredCount === 1 ? "" : "s"}`}
						</button>
					) : null}
				</div>
			) : null}
			<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{ordered.map((group) => {
					const tps = speed.get(group.providerId);
					return (
						<OfferCard
							key={group.providerId}
							modelId={modelId}
							provider={{
								id: group.providerId,
								name: group.provider?.name,
								modelCardBadge: group.provider?.modelCardBadge,
							}}
							offers={group.offers}
							providerHref={`/providers/${encodeURIComponent(group.providerId)}`}
							headerExtra={
								tps ? (
									<p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
										<Gauge className="size-3 text-amber-500" />
										{formatNumber(Math.round(tps))} tokens/s
									</p>
								) : null
							}
						/>
					);
				})}
			</div>
		</div>
	);
}
